import React from "react";
import { Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SIDE_TUG_METRIC_TREND } from "../../../../routes/routerPath";
import { pageLocale } from "../../../../utils/locale";
import "./metricdetailexpansionv2.scss";

const MetricDetailExpansionV2 = ({
  metric,
  value,
  range,
  onShowDefinition,
  trendPath = SIDE_TUG_METRIC_TREND,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mv2 = pageLocale("metricsv2");
  const mv2BasedOn = pageLocale("metricsv2_fallrisk_based_on");
  const mv2FallRiskDescriptions = pageLocale("metricsv2_fallrisk_descriptions");

  // Calculate bar graph segments
  const getBarSegments = () => {
    if (!range || !range.length) return [];

    let min = 0;
    let max = 0;
    range.forEach((r) => {
      if (r.minMax) {
        min = Math.min(min, r.minMax[0]);
        max = Math.max(max, r.minMax[1]);
      }
    });

    if (max === 0) {
      max = Math.max(value * 2, 10);
    }

    const segments = range.map((r) => {
      const width = r.minMax
        ? ((r.minMax[1] - r.minMax[0]) / (max - min)) * 100
        : 33.33;
      const left = r.minMax ? ((r.minMax[0] - min) / (max - min)) * 100 : 0;
      const color =
        r.severity === "good"
          ? "#11b981"
          : r.severity === "average"
          ? "#f59e0b"
          : "#e53e3e";

      return {
        width: `${width}%`,
        left: `${left}%`,
        color,
        minMax: r.minMax,
        type: r.type,
        severity: r.severity,
      };
    });

    // Find average (orange) segment boundaries for display
    const averageSegment = segments.find((s) => s.severity === "average");
    const averageMin = averageSegment?.minMax?.[0];
    const averageMax = averageSegment?.minMax?.[1];

    return {
      segments,
      min,
      max,
      currentValue: value,
      averageMin,
      averageMax,
    };
  };

  const handleViewTrend = () => {
    const mapping = {
      type: metric.category || "tug",
      filterField: metric.apiField || "time",
    };
    const currentParams = new URLSearchParams(searchParams);

    // Pass the original metric so MetricTrendView gets the right visual config
    currentParams.set("metric", metric.metric);
    // Pass the actual V5 params
    currentParams.set("type", mapping.type);
    currentParams.set("filterField", mapping.filterField);

    navigate(`${trendPath}?${currentParams.toString()}`, {
      state: { rangeData: range },
    });
  };

  const barData = getBarSegments();

  return (
    <div className="metric-detail-expansion-v2">
      {barData.segments && barData.segments.length > 0 && (
        <div className="metric-scale-section">
          <div className="scale-bar-container">
            {barData.currentValue !== null &&
              barData.currentValue !== undefined && (
                <div
                  className="value-marker"
                  style={{
                    left: `${Math.max(
                      0,
                      Math.min(
                        100,
                        ((barData.currentValue - barData.min) /
                          (barData.max - barData.min)) *
                          100,
                      ),
                    )}%`,
                  }}
                >
                  <div className="marker-triangle" />
                  <div className="marker-line" />
                </div>
              )}
            <div className="scale-bar">
              {barData.segments.map((segment, index) => (
                <div
                  key={index}
                  className="scale-segment"
                  style={{
                    width: segment.width,
                    left: segment.left,
                    backgroundColor: segment.color,
                  }}
                />
              ))}
            </div>
            <div className="scale-numbers">
              {barData.averageMin !== undefined &&
                barData.averageMax !== undefined && (
                  <>
                    <Typography
                      className="scale-number scale-number-average-min"
                      style={{
                        left: `${
                          ((barData.averageMin - barData.min) /
                            (barData.max - barData.min)) *
                          100
                        }%`,
                      }}
                    >
                      {(() => {
                        const formatNumber = (val) => {
                          if (
                            metric.unit === "steps/min" ||
                            metric.unit === "count"
                          ) {
                            return Math.round(val);
                          }
                          const num = Number(val);
                          if (metric.unit === "unitless") {
                            return num % 1 === 0
                              ? num.toString()
                              : num.toFixed(2);
                          }
                          return num % 1 === 0
                            ? num.toString()
                            : num.toFixed(1);
                        };
                        return formatNumber(barData.averageMin);
                      })()}
                    </Typography>
                    <Typography
                      className="scale-number scale-number-average-max"
                      style={{
                        left: `${
                          ((barData.averageMax - barData.min) /
                            (barData.max - barData.min)) *
                          100
                        }%`,
                      }}
                    >
                      {(() => {
                        const formatNumber = (val) => {
                          if (
                            metric.unit === "steps/min" ||
                            metric.unit === "count"
                          ) {
                            return Math.round(val);
                          }
                          const num = Number(val);
                          if (metric.unit === "unitless") {
                            return num % 1 === 0
                              ? num.toString()
                              : num.toFixed(2);
                          }
                          return num % 1 === 0
                            ? num.toString()
                            : num.toFixed(1);
                        };
                        return formatNumber(barData.averageMax);
                      })()}
                    </Typography>
                  </>
                )}
            </div>
          </div>
        </div>
      )}

      <div className="fall-risk-relevance-section">
        <div className="fall-risk-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.0001 18.3327C11.0946 18.334 12.1787 18.1191 13.1899 17.7002C14.2011 17.2813 15.1196 16.6668 15.8926 15.8919C16.6675 15.1189 17.2821 14.2004 17.701 13.1892C18.1198 12.1779 18.3348 11.0939 18.3334 9.99936C18.3348 8.90481 18.1198 7.82078 17.701 6.80956C17.2821 5.79834 16.6675 4.87984 15.8926 4.10686C15.1196 3.33192 14.2011 2.71736 13.1899 2.29849C12.1787 1.87961 11.0946 1.66467 10.0001 1.66602C8.90554 1.66467 7.82152 1.87961 6.81029 2.29849C5.79907 2.71736 4.88058 3.33192 4.10759 4.10686C3.33265 4.87984 2.7181 5.79834 2.29922 6.80956C1.88034 7.82078 1.6654 8.90481 1.66675 9.99936C1.6654 11.0939 1.88034 12.1779 2.29922 13.1892C2.7181 14.2004 3.33265 15.1189 4.10759 15.8919C4.88058 16.6668 5.79907 17.2813 6.81029 17.7002C7.82152 18.1191 8.90554 18.334 10.0001 18.3327Z"
              stroke="#3b82f6"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.0002 15.4154C10.2764 15.4154 10.5414 15.3056 10.7367 15.1103C10.9321 14.9149 11.0418 14.65 11.0418 14.3737C11.0418 14.0974 10.9321 13.8325 10.7367 13.6371C10.5414 13.4418 10.2764 13.332 10.0002 13.332C9.7239 13.332 9.45894 13.4418 9.26359 13.6371C9.06824 13.8325 8.9585 14.0974 8.9585 14.3737C8.9585 14.65 9.06824 14.9149 9.26359 15.1103C9.45894 15.3056 9.7239 15.4154 10.0002 15.4154Z"
              fill="#3b82f6"
            />
            <path
              d="M10 5V11.6667"
              stroke="#3b82f6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Typography className="fall-risk-heading">
            {mv2?.fall_risk_relevance}
          </Typography>
        </div>
        <Typography className="fall-risk-subheading">
          {mv2?.based_on_prefix}
          {mv2BasedOn?.[metric.metric] || metric.metric.replace(/_/g, " ")}
          {mv2?.performance_suffix}
        </Typography>
        <div className="fall-risk-content">
          <Typography className="fall-risk-text">
            {mv2FallRiskDescriptions?.[metric.metric] ||
              metric.fallRiskRelevance ||
              metric.details}
          </Typography>
          {onShowDefinition && (
            <div style={{ marginTop: "12px" }}>
              <a
                href="#"
                className="define-link"
                onClick={(e) => {
                  e.preventDefault();
                  onShowDefinition();
                }}
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#2563eb",
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                &gt; {mv2?.how_its_defined}
              </a>
            </div>
          )}
        </div>
      </div>

      <button className="view-trend-button" onClick={handleViewTrend}>
        {mv2?.view_performance_trend}
      </button>
    </div>
  );
};

export default MetricDetailExpansionV2;
